import React, { useEffect, useRef, useState } from "react";
import { Save, Upload, Copy, Smartphone, Palette, Trash2, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { THEME_PRESETS } from "@/lib/themes";

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_LOGO_BYTES = 200 * 1024;

export default function TenantBrandingEditor({ tenant, onSaved }) {
  const initial = () => ({
    name: tenant.name || "",
    brand_name: tenant.brand_name || "",
    slug: tenant.slug || "",
    logo_url: tenant.logo_url || "",
    primary_color: tenant.primary_color || "#0F172A",
    accent_color: tenant.accent_color || "#D4AF37",
    theme_key: tenant.theme_key || "royal_navy",
    subscription_amount: tenant.subscription_amount ?? 9999,
    renewal_amount: tenant.renewal_amount ?? 9999,
  });
  const [form, setForm] = useState(initial);
  const [base, setBase] = useState(initial); // snapshot to diff against
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    const next = initial();
    setForm(next);
    setBase(next);
  }, [tenant.id, tenant.name, tenant.brand_name, tenant.slug, tenant.logo_url, tenant.primary_color, tenant.accent_color, tenant.theme_key, tenant.subscription_amount, tenant.renewal_amount]);

  const applyTheme = (preset) => {
    setForm((f) => ({ ...f, theme_key: preset.key, primary_color: preset.primary, accent_color: preset.accent }));
  };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(`Logo too large (${(file.size / 1024).toFixed(0)} KB). Max 200 KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const slugValid = !form.slug || SLUG_RE.test(form.slug);

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    if (!slugValid) return toast.error("Slug must be lowercase letters, digits and hyphens");
    // Build a dirty-only payload so we never overwrite fields the user didn't touch.
    const body = {};
    const keys = ["name", "brand_name", "slug", "primary_color", "accent_color", "theme_key"];
    keys.forEach((k) => {
      if ((form[k] ?? "") !== (base[k] ?? "")) body[k] = form[k] || null;
    });
    // Numeric subscription fields
    ["subscription_amount", "renewal_amount"].forEach((k) => {
      const newVal = parseFloat(form[k]);
      const oldVal = parseFloat(base[k]);
      if (!isNaN(newVal) && newVal !== oldVal) body[k] = newVal;
    });
    // logo_url: only include if changed; allow null (clear)
    if ((form.logo_url || null) !== (base.logo_url || null)) {
      body.logo_url = form.logo_url || null;
    }
    if (Object.keys(body).length === 0) {
      toast.info("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/super-admin/tenants/${tenant.id}`, body);
      toast.success("Tenant updated");
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = form.slug ? `${window.location.origin}/?tenant=${form.slug}` : "";
  const qrUrl = shareUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=2&data=${encodeURIComponent(shareUrl)}` : "";

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("PWA link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow" data-testid="tenant-branding-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-extrabold text-lg">Branding & PWA</div>
          <div className="text-[11px] text-slate-500">Edit name, logo, theme and shareable PWA link</div>
        </div>
        <button data-testid="save-tenant-branding" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-tenant-primary text-white px-4 py-2 rounded-xl font-bold text-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        {/* LEFT: Identity & Logo */}
        <div className="space-y-3">
          <Field label="Tenant Name" testId="t-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Brand Name" testId="t-brand" value={form.brand_name} onChange={(v) => setForm({ ...form, brand_name: v })} />
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">URL Slug</label>
            <div className="mt-1.5 flex items-center gap-2 border-2 rounded-xl px-3 py-2 bg-white" style={{ borderColor: slugValid ? "#E2E8F0" : "#F43F5E" }}>
              <span className="text-[11px] text-slate-400 font-semibold">/?tenant=</span>
              <input data-testid="t-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} className="flex-1 outline-none font-bold text-sm" placeholder="my-firm" />
            </div>
            {!slugValid && <div className="text-[10px] text-rose-500 mt-1">Lowercase letters, digits and hyphens only</div>}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Brand Logo</label>
            <div className="mt-1.5 border-2 border-dashed border-slate-200 rounded-xl p-3 flex items-center gap-3">
              <div className="w-16 h-16 rounded-xl bg-slate-50 grid place-items-center overflow-hidden border border-slate-200">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="logo preview" className="w-full h-full object-contain" data-testid="logo-preview" />
                ) : (
                  <Smartphone className="w-6 h-6 text-slate-300" />
                )}
              </div>
              <div className="flex-1">
                <input ref={fileInput} type="file" accept="image/*" onChange={onPickLogo} className="hidden" data-testid="logo-file-input" />
                <div className="flex gap-2 flex-wrap">
                  <button data-testid="logo-pick-btn" onClick={() => fileInput.current?.click()} className="text-[11px] inline-flex items-center gap-1 bg-slate-900 text-white px-3 py-1.5 rounded-lg font-bold">
                    <Upload className="w-3 h-3" /> Upload
                  </button>
                  {form.logo_url && (
                    <button data-testid="logo-clear-btn" onClick={() => setForm({ ...form, logo_url: "" })} className="text-[11px] inline-flex items-center gap-1 bg-rose-50 text-rose-600 px-3 py-1.5 rounded-lg font-bold">
                      <Trash2 className="w-3 h-3" /> Clear
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5">PNG/JPG/SVG · square recommended · max 200 KB</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Theme + PWA share */}
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 inline-flex items-center gap-1">
              <Palette className="w-3 h-3" /> Theme Preset
            </label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {THEME_PRESETS.map((p) => {
                const active = form.theme_key === p.key;
                return (
                  <button
                    key={p.key}
                    data-testid={`theme-${p.key}`}
                    onClick={() => applyTheme(p)}
                    className={`relative rounded-xl px-3 py-2.5 text-left border-2 transition-all ${active ? "ring-2 ring-offset-1 ring-slate-900 border-transparent" : "border-slate-100 hover:border-slate-200"}`}
                    style={{ background: `linear-gradient(135deg, ${p.primary} 0%, ${p.primary} 60%, ${p.accent} 100%)` }}
                  >
                    {active && <Check className="w-3.5 h-3.5 absolute top-1.5 right-1.5 text-white" />}
                    <div className="text-[11px] font-extrabold text-white">{p.label}</div>
                    <div className="text-[9px] uppercase tracking-widest opacity-80 mt-0.5" style={{ color: p.accent }}>{p.key}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <ColorPicker label="Primary" testId="t-primary" value={form.primary_color} onChange={(v) => setForm({ ...form, primary_color: v, theme_key: "custom" })} />
            <ColorPicker label="Accent" testId="t-accent" value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v, theme_key: "custom" })} />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="subscription-card">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Subscription Pricing</div>
            <p className="text-[11px] text-slate-500 mt-1">Charged by Super Admin. Tenant Admin pays via their portal.</p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <PriceField testId="t-subscription-amount" label="One-time (₹)" value={form.subscription_amount} onChange={(v) => setForm({ ...form, subscription_amount: v })} />
              <PriceField testId="t-renewal-amount" label="Renewal (₹/mo)" value={form.renewal_amount} onChange={(v) => setForm({ ...form, renewal_amount: v })} />
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="pwa-share-card">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">PWA Share Link</div>
            {shareUrl ? (
              <>
                <div className="mt-2 flex items-start gap-3">
                  <img src={qrUrl} alt="QR" className="w-24 h-24 rounded-lg bg-white border border-slate-200" data-testid="pwa-qr" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-slate-500">Send this link to {form.brand_name || form.name} — they can install the PWA on their phone.</div>
                    <div className="mt-2 flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1.5">
                      <code data-testid="pwa-share-url" className="text-[10px] truncate flex-1">{shareUrl}</code>
                      <button data-testid="copy-share-link" onClick={copy} className="text-tenant-primary inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider">
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <a href={shareUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-blue-600">
                      <ExternalLink className="w-3 h-3" /> Open in new tab
                    </a>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-[11px] text-slate-400 italic mt-2">Save a slug to generate the PWA share link.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</label>
    <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary" />
  </div>
);

const ColorPicker = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</label>
    <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-xl px-2 py-1.5 bg-white">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 outline-none font-mono text-[12px]" />
    </div>
  </div>
);

const PriceField = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</label>
    <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-xl px-3 py-2 bg-white">
      <span className="text-slate-400 font-bold">₹</span>
      <input data-testid={testId} type="number" min="0" step="1" value={value ?? 0} onChange={(e) => onChange(e.target.value)} className="flex-1 outline-none font-bold" />
    </div>
  </div>
);
